import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { EASE } from "@/components/Reveal";
import { getConsent, saveConsent } from "@/consent";

const Toggle = ({ checked, disabled = false, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange && onChange(!checked)}
    data-testid={testid}
    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-300 ${
      checked ? "bg-rot" : "bg-anthrazit/25"
    } ${disabled ? "cursor-not-allowed opacity-70" : ""}`}
  >
    <span
      className={`absolute top-1 h-5 w-5 rounded-full bg-creme shadow transition-all duration-300 ${
        checked ? "left-6" : "left-1"
      }`}
    />
  </button>
);

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [view, setView] = useState("main");
  const [external, setExternal] = useState(false);

  useEffect(() => {
    if (!getConsent()) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const open = () => {
      setExternal(getConsent()?.external ?? false);
      setView("settings");
      setVisible(true);
    };
    window.addEventListener("open-cookie-settings", open);
    return () => window.removeEventListener("open-cookie-settings", open);
  }, []);

  const choose = (value) => {
    saveConsent(value);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Cookie-Hinweis"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="fixed inset-x-4 bottom-4 z-[70] mx-auto max-h-[85vh] overflow-y-auto rounded-[1.75rem] border border-anthrazit/10 bg-creme/95 p-6 shadow-[0_20px_60px_-20px_rgba(53,51,48,0.35)] backdrop-blur-md md:bottom-6 md:p-8"
        >
          {view === "main" ? (
            <>
              <p className="font-serif text-xl text-anthrazit md:text-2xl">Cookies</p>
              <p className="mt-3 text-sm leading-relaxed text-anthrazit/75">
                Wir verwenden Cookies und ähnliche Technologien, um die grundlegende Funktion
                unserer Website zu gewährleisten und – sofern du zustimmst – externe Inhalte wie
                Google Maps und YouTube einzubinden. Du kannst selbst entscheiden, welchen
                Kategorien du zustimmst. Deine Auswahl kannst du jederzeit über die
                Cookie-Einstellungen ändern. Weitere Informationen findest du in unserer{" "}
                <Link
                  to="/datenschutz"
                  data-testid="cookie-datenschutz-link"
                  className="text-rot underline underline-offset-4"
                >
                  Datenschutzerklärung
                </Link>
                .
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  data-testid="cookie-accept-all-button"
                  onClick={() => choose(true)}
                  className="rounded-full bg-rot px-7 py-2.5 text-sm tracking-[0.1em] text-creme transition-colors duration-300 hover:bg-anthrazit"
                >
                  Alle akzeptieren
                </button>
                <button
                  type="button"
                  data-testid="cookie-necessary-button"
                  onClick={() => choose(false)}
                  className="rounded-full border border-anthrazit/25 px-7 py-2.5 text-sm tracking-[0.1em] text-anthrazit transition-colors duration-300 hover:border-rot hover:text-rot"
                >
                  Nur notwendige
                </button>
                <button
                  type="button"
                  data-testid="cookie-settings-button"
                  onClick={() => setView("settings")}
                  className="ml-auto text-xs uppercase tracking-[0.2em] text-rot underline-offset-4 hover:underline"
                >
                  Einstellungen
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="font-serif text-xl text-anthrazit md:text-2xl">Cookie-Einstellungen</p>
              <div className="mt-5 space-y-4">
                <div className="rounded-2xl border border-anthrazit/10 bg-karte p-5">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm font-semibold tracking-[0.08em] text-anthrazit">
                      Notwendige Cookies
                    </p>
                    <div className="flex items-center gap-3">
                      <span className="text-xs uppercase tracking-[0.15em] text-rot">
                        Immer aktiv
                      </span>
                      <Toggle checked disabled testid="toggle-necessary" />
                    </div>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-anthrazit/70">
                    Diese Technologien sind erforderlich, damit die Website technisch funktioniert.
                    Sie können nicht deaktiviert werden.
                  </p>
                </div>
                <div className="rounded-2xl border border-anthrazit/10 bg-karte p-5">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm font-semibold tracking-[0.08em] text-anthrazit">
                      Externe Medien
                    </p>
                    <Toggle checked={external} onChange={setExternal} testid="toggle-external" />
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-anthrazit/70">
                    Auf unserer Website werden Inhalte von Drittanbietern wie Google Maps und
                    YouTube eingebunden. Wenn du diese Inhalte aktivierst, kann eine Verbindung zu
                    den Servern der jeweiligen Anbieter hergestellt und dabei können Daten
                    übertragen werden.
                  </p>
                  <ul className="mt-3 space-y-1.5 text-sm text-anthrazit/70">
                    <li className="flex items-center gap-2.5">
                      <span className="h-1 w-1 rounded-full bg-rot" />
                      Google Maps
                    </li>
                    <li className="flex items-center gap-2.5">
                      <span className="h-1 w-1 rounded-full bg-rot" />
                      YouTube
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  data-testid="cookie-save-button"
                  onClick={() => choose(external)}
                  className="rounded-full bg-rot px-7 py-2.5 text-sm tracking-[0.1em] text-creme transition-colors duration-300 hover:bg-anthrazit"
                >
                  Auswahl speichern
                </button>
                <button
                  type="button"
                  data-testid="cookie-accept-all-settings-button"
                  onClick={() => choose(true)}
                  className="rounded-full border border-anthrazit/25 px-7 py-2.5 text-sm tracking-[0.1em] text-anthrazit transition-colors duration-300 hover:border-rot hover:text-rot"
                >
                  Alle akzeptieren
                </button>
                <button
                  type="button"
                  data-testid="cookie-back-button"
                  onClick={() => setView("main")}
                  className="ml-auto text-xs uppercase tracking-[0.2em] text-rot underline-offset-4 hover:underline"
                >
                  Zurück
                </button>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
